import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container, Typography, Card, CardContent, Box, Button,
  CircularProgress, Alert, Grid, Chip, Avatar, Divider,
  Paper, Stack, IconButton, Tooltip
} from '@mui/material';
import {
  ArrowBack, Person, AttachMoney, Schedule, CheckCircle,
  Cancel, Description, TrendingUp, Star, AutoAwesome
} from '@mui/icons-material';
import proposalService from '../services/proposalService';
import ProposalReviewDialog from '../components/proposals/ProposalReviewDialog';
import { brand } from '../theme/colors';

const ProjectProposalsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [projectTitle, setProjectTitle] = useState('');

  useEffect(() => {
    loadProposals();
  }, [id]);

  const loadProposals = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await proposalService.getProposalsForProject(id);
      
      if (response.success) {
        setProposals(response.data || []);
        
        // Get project title from first proposal or fetch separately
        if (response.data && response.data.length > 0) {
          setProjectTitle(response.data[0].project_title || '');
        }
      }
    } catch (err) {
      console.error('Error loading proposals:', err);
      setError(err.message || 'Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': '#2196f3',
      'under_review': '#ff9800',
      'accepted': '#4caf50',
      'rejected': '#f44336',
      'withdrawn': '#9e9e9e'
    };
    return colors[status] || '#9e9e9e';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'submitted': 'Submitted',
      'under_review': 'Under Review',
      'accepted': 'Accepted',
      'rejected': 'Rejected',
      'withdrawn': 'Withdrawn'
    };
    return labels[status] || status;
  };

  const handleReviewProposal = (proposal) => {
    setSelectedProposal(proposal);
    setShowReviewDialog(true);
  };

  const handleProposalAction = async (action) => {
    setShowReviewDialog(false);
    setSelectedProposal(null);
    if (action === 'accepted') {
      // Navigate to project detail page — escrow setup will auto-prompt there
      navigate(`/projects/${id}?action=fund`);
    } else {
      await loadProposals();
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading proposals...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/projects/${id}`)}
          sx={{ mb: 2 }}
        >
          Back to Project
        </Button>
        
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Proposals for {projectTitle || 'Project'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and compare contractor proposals for your project
        </Typography>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {proposals.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Description sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                No Proposals Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
                Your project is open for bids, but no contractors have submitted proposals yet. 
                This is normal for new projects. Contractors typically submit proposals within 24-48 hours.
              </Typography>
              
              <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2, maxWidth: 700, mx: 'auto' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  What happens next?
                </Typography>
                <Stack spacing={2} sx={{ textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <CheckCircle color="primary" />
                    <Typography variant="body2">
                      Contractors review your project details and submit detailed proposals
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <CheckCircle color="primary" />
                    <Typography variant="body2">
                      Each proposal includes milestones, timeline, and pricing breakdown
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <CheckCircle color="primary" />
                    <Typography variant="body2">
                      You'll receive notifications when new proposals arrive
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <CheckCircle color="primary" />
                    <Typography variant="body2">
                      Compare proposals and select the best contractor for your project
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/contractors')}
                  sx={{
                    background: brand.gradient,
                    '&:hover': {
                      background: brand.gradientHover
                    }
                  }}
                >
                  Invite Contractors
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate(`/projects/${id}`)}
                >
                  Back to Project
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Proposals
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {proposals.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Average Quote
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {formatCurrency(
                      proposals.reduce((sum, p) => sum + (parseFloat(p.quote_amount) || 0), 0) / proposals.length
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {proposals.filter(p => p.status === 'submitted').length} Pending Review
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Proposals List */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            All Proposals
          </Typography>
          
          <Stack spacing={2}>
            {proposals.map((proposal) => (
              <Card key={proposal.id} elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Contractor Avatar */}
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem'
                      }}
                    >
                      {proposal.contractor_first_name?.[0]}{proposal.contractor_last_name?.[0]}
                    </Avatar>

                    {/* Proposal Details */}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {proposal.contractor_first_name} {proposal.contractor_last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {proposal.years_experience} years experience
                          </Typography>
                        </Box>
                        <Chip
                          label={getStatusLabel(proposal.status)}
                          sx={{
                            bgcolor: getStatusColor(proposal.status),
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoney fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Quote Amount
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {formatCurrency(proposal.quote_amount, proposal.currency)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} sm={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Timeline
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {proposal.timeline_days} days
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUp fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Team Size
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {proposal.team_size || 'N/A'} people
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Submitted
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {formatDate(proposal.submitted_at)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>

                      {proposal.proposal_text && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {proposal.proposal_text}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => handleReviewProposal(proposal)}
                          sx={{
                            background: brand.gradient,
                            '&:hover': {
                              background: brand.gradientHover
                            }
                          }}
                        >
                          Review Proposal
                        </Button>
                        {proposal.status === 'submitted' && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              // Handle reject
                              handleReviewProposal(proposal);
                            }}
                          >
                            Reject
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}

      {/* Proposal Review Dialog */}
      {showReviewDialog && selectedProposal && (
        <ProposalReviewDialog
          open={showReviewDialog}
          onClose={() => {
            setShowReviewDialog(false);
            setSelectedProposal(null);
          }}
          proposal={selectedProposal}
          onProposalAction={handleProposalAction}
        />
      )}
    </Container>
  );
};

export default ProjectProposalsPage;
